import { useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useFormik } from 'formik'
import { TableContext } from 'Context/Table'
import { ShippingListViewContext } from '../List/Context'
import { ShippingFormModalContext } from './Context'
import { useShippingRequestStore } from 'Store/ShippingRequest'
import { useShippingStore, INITIAL_STATE } from 'Store/Shipping'
import * as Yup from 'yup'
import moment from 'moment'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'

import PileInput from './Components/PileInput'
import Swal from 'sweetalert2'

const VALIDATION_SCHEMA = Yup.object().shape({
  startShippingAt: Yup.date().required('Required'),
  endShippingAt: Yup.date()
    .required('Required')
    .min(Yup.ref('startShippingAt'), 'End date must be after start date'),
  transferDocumentNo: Yup.string().required('Required'),
})

const ViewModel = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { shippingRequestId, detailId } = useParams()
  const isCreate = location.pathname.includes('create')
  const isView = location.search.includes('view')
  const {
    // currentSorting,
    updateLoading,
    updateError,
    updatePagination,
    updateSorting,
    resetState: resetTableState,
  } = useContext(TableContext)
  const { isOpenShippingModal, toggleShippingModal } = useContext(ShippingListViewContext)
  const { formType } = useContext(ShippingFormModalContext)
  const { selected, getShippingRequest } = useShippingRequestStore(
    state => ({
      selected: state.selected,
      getAll: state.getAll,
      getShippingRequest: state.getOne,
    }),
    shallow
  )

  const { createShipping, getOneShipping, updateShipping, updateShippingItem } = useShippingStore(
    state => ({
      getOneShipping: state.getOne,
      createShipping: state.create,
      updateShipping: state.edit,
      updateShippingItem: state.editShippingItem,
    }),
    shallow
  )

  const [staticShippingRequest, setStaticShippingRequest] = useState<any>({
    jobStatus: '',
    saleOrderCode: '',
    division: '',
    customerTitle: '',
    status: '',
    saleOrderId: '',
  })

  const setupTable = () => {
    updatePagination(true)
    updateSorting('itemCode', true)
  }

  const submit = async (values: any) => {
    if (isCreate) {
      if (values.pilePerRound && isNaN(values.pilePerRound)) {
        values.pilePerRound = 0
      }
      const body: { [key: string]: any } = {
        ...values,
        shippingRequestId: shippingRequestId,
        startShippingAt: moment(values.startShippingAt).format('YYYY-MM-DD HH:mm:ss'),
        endShippingAt: moment(values.endShippingAt).format('YYYY-MM-DD HH:mm:ss'),
        shippingItems: values.shippingItems.filter((s: any) => Number(s.deliverQuantity) > 0),
      }

      const { success, data } = await createShipping(body)
      if (!success) {
        toast.error(data)
        return
      } else {
        toast.success('เพิ่มการขนส่งสำเร็จ')
        navigate(`/shipping-request/${shippingRequestId}`)
      }
    } else if (!isCreate && detailId) {
      const body: { [key: string]: any } = {}

      Object.keys(values).forEach(key => {
        if (key === 'shippingItems') {
          const newShippingItems = values[key]
          if (newShippingItems.length === 0) return
          body[key] = newShippingItems
        } else if (['startShippingAt', 'endShippingAt'].includes(key)) {
          body[key] = moment(values[key]).format('YYYY-MM-DD HH:mm:ss')
        } else {
          body[key] = values[key]
        }
      })

      if (Object.keys(body).length === 0) {
        toast.error('No change')
        return
      }

      const { data, success } = await updateShipping(detailId ? parseInt(detailId) : 0, body)
      if (!success) {
        toast.error(data)
        return
      } else {
        toast.success('Update shipping successfully')
        fetchData()
      }
    }
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: INITIAL_STATE,
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: async (values, { resetForm }) => {
      await submit(values)
    },
  })
  const hasItemInvalid = useMemo(() => {
    if (
      formik.values.shippingItems.some(s => Number(s?.shippingQuantity) > s?.availableForShipment)
    ) {
      return true
    }

    return false
  }, [formik.values.shippingItems])

  const onSubmitShippingItem = async () => {
    const result = await Swal.fire({
      title: 'ยืนยันการแก้ไข',
      text: 'หากมีการแก้ไขจำเป็นต้องพิมพ์ QR Code ใหม่ทั้งหมด',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก'
    });
  
    if (!result.isConfirmed) return;
    const body = {shippingItems:formik.values.shippingItems.filter((s: any) => Number(s.deliverQuantity) > 0)}

    if(!detailId){
      toast.error('Invalid shipping id')
      return

    }
    const { data, success } = await updateShippingItem(parseInt(detailId), body)
      if (!success) {
        toast.error(data)
        return
      } else {
        toast.success('Update shipping successfully')
        fetchData()
      }
  }

  const fetchData = async () => {
    updateLoading(true)
    //
    if (shippingRequestId && detailId) {
      const { data, success } = await getShippingRequest(
        shippingRequestId ? parseInt(shippingRequestId) : 0
      )

      if (!success) {
        toast.error(data)
        toggleShippingModal(false)
        updateError(true)
        updateLoading(false)
        return
      } else {
        const { data: dataShipping, success: successShipping } = await getOneShipping(
          parseInt(detailId)
        )
        if (!successShipping) {
          toast.error(dataShipping)
          toggleShippingModal(false)
          updateError(true)
          updateLoading(false)
          return
        } else {
          const masterItems = data.project.saleOrder.saleOrderItems
          const items = dataShipping.shippingItems
          const findedShippingOrder = data.shippingOrders.find(
            (s: any) => s.id === parseInt(detailId)
          )

          const shippingItems = masterItems.map((item: any) => {
            const findedItem = items.find((i: any) => i.pileCode === item.itemCode)
            if (findedItem) {
              return {
                ...item,
                ...findedItem,
              }
            } else {
              return item
            }
          })

          // Content
          formik.setFieldValue('shippingItems', shippingItems)

          formik.setFieldValue('startShippingAt', findedShippingOrder.startShippingAt)
          formik.setFieldValue('endShippingAt', findedShippingOrder.endShippingAt)
          formik.setFieldValue('pilePerRound', findedShippingOrder.pilePerRound ?? '')
          formik.setFieldValue('driverFullName', findedShippingOrder.driverFullName ?? '')
          formik.setFieldValue('deliveryTime', findedShippingOrder.deliveryTime)
          formik.setFieldValue('deliveryBy', findedShippingOrder.deliveryBy)
          formik.setFieldValue('vehicleNo', findedShippingOrder.vehicleNo)
          formik.setFieldValue('transferDocumentNo', findedShippingOrder.transferDocumentNo)

          // Header
          setStaticShippingRequest({
            jobStatus: findedShippingOrder?.jobStatus,
            saleOrderId: data.project.saleOrder.id,
            saleOrderCode: data.project.saleOrder.code,
            division: data.project.saleOrder.division,
            customerTitle: data.project.saleOrder.customerTitle,
            status: data.status,
          })
          updateLoading(false)
          updateError(false)
        }
      }
    } else if (isCreate && shippingRequestId && !detailId) {
      const { data, success } = await getShippingRequest(
        shippingRequestId ? parseInt(shippingRequestId) : 0
      )

      if (!success) {
        toast.error(data)
        toggleShippingModal(false)
        updateError(true)
        updateLoading(false)
        return
      } else {
        const items = data.project.saleOrder.saleOrderItems

        // Header
        setStaticShippingRequest({
          jobStatus: '',
          saleOrderId: data.project.saleOrder.id,
          saleOrderCode: data.project.saleOrder.code,
          division: data.project.saleOrder.division,
          customerTitle: data.project.saleOrder.customerTitle,
          status: data.status,
        })

        // Items
        formik.setFieldValue('shippingItems', items)
        updateLoading(false)
        updateError(false)
      }
    }
  }

  const handleClose = () => {
    toggleShippingModal(false)
    navigate('/shipping-request')
    formik.resetForm()
  }
  const onChangeDeliverItem = (pileId: number, field: string, value: any) => {
    const isNumber = !isNaN(value)
    if (!isNumber) return
    const newSaleOrderItems = [...formik.values.shippingItems]
    const index = newSaleOrderItems.findIndex(item => item.id === pileId)
    newSaleOrderItems[index][field] = value
    newSaleOrderItems[index]['isEdit'] = true

    formik.setFieldValue('shippingItems', newSaleOrderItems)
  }
  const onChangePile = (idx: number, key: string, value: string) => {
    const newPiles = [...formik.values.shippingItems]
    newPiles[idx][key] = value
    formik.setFieldValue('shippingItems', newPiles)
  }

  const modalsRoot = document.getElementById('root-modals') || document.body

  const PILE_ITEMS_CONFIGS: any[] = [
    {
      Header: 'รหัสเสา',
      accessor: 'itemCode',
      width: 80,
      minWidth: 80,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value}</span>
      },
    },
    {
      Header: 'ชื่อเสา',
      accessor: 'itemTitle',
      width: 80,
      minWidth: 80,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value}</span>
      },
    },
     {
      Header: 'โรงงาน',
      accessor: 'factory',
      width: 80,
      minWidth: 80,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value}</span>
      },
    },
    {
      Header: 'จำนวนที่ขนส่ง',
      headerRight: true,
      accessor: 'deliverQuantity',
      width: 120,
      minWidth: 120,
      field: 'deliverQuantity',
      onChange: onChangeDeliverItem,
      isView: isView,
      Cell: PileInput,
    },
  ]

  useEffect(() => {
    setupTable()
    return () => {
      resetTableState()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailId, isCreate, isView, isOpenShippingModal])

  return {
    isCreate,
    isView,
    modalsRoot,
    isOpenShippingModal,
    formType,
    handleClose,
    staticShippingRequest,
    formik,
    shippingRequest: selected,
    shippingRequestId,
    onSubmitShippingItem,
    // saleOrder: saleOrder,
    onChangePile,
    PILE_ITEMS_CONFIGS,
    hasItemInvalid,
  }
}

export default ViewModel
